import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi, type PropertyGroupResponse } from '@/shared/api';
import { saveFailureMessage } from './objectSaveFailure';
import { objectUpdateRequest, type ObjectPatch } from './objectUpdateRequest';
import { useObjectEditSaved } from './useObjectEditSaved';

type Target = Parameters<typeof useObjectEditSaved>[1];

/**
 * Speichert den Teil einer Karte über den bestehenden Objekt-Vertrag. Eine Ablehnung bleibt als
 * Satz im Sheet stehen, statt nach wenigen Sekunden mit einem Toast zu verschwinden.
 */
export function useObjectUpdate(group: PropertyGroupResponse, target: Target, onDone: () => void) {
  const api = useApi();
  const queryClient = useQueryClient();
  const onSaved = useObjectEditSaved(group.id, target);
  const [error, setError] = useState<string | null>(null);
  const [unanswered, setUnanswered] = useState(false);

  const mutation = useMutation({
    mutationFn: async (patch: ObjectPatch) => {
      // Die Pflichtfelder kommen aus dem Serverstand von jetzt, nicht aus dem beim Öffnen: sonst
      // überschriebe das Speichern der Zeiten eine inzwischen geänderte Adresse oder einen frisch
      // ermittelten Pin — der Vertrag verlangt Name und Adresse bei jeder Anfrage.
      const current = await queryClient.fetchQuery({
        queryKey: ['property-groups', group.id],
        queryFn: () => api.propertyGroups.get(group.id),
        staleTime: 0,
      });
      return api.propertyGroups.update(group.id, objectUpdateRequest(current, patch));
    },
    // Offline pausiert TanStack sonst still, und „Wird gespeichert …“ endete nie.
    networkMode: 'always',
    onMutate: () => {
      setError(null);
      setUnanswered(false);
    },
    onSuccess: async (saved) => {
      // Der gespeicherte Stand gilt sofort, auch wenn das Nachladen danach scheitert.
      queryClient.setQueryData(['property-groups', saved.id], saved);
      await onSaved(saved);
      toast.success(saved.id === group.id ? 'Gespeichert' : `Zusammengeführt mit „${saved.name}"`);
      onDone();
    },
    onError: async (failure) => {
      const problem = await toApiError(failure);
      setUnanswered(problem.status === 0);
      setError(saveFailureMessage(problem));
    },
  });

  return {
    save: mutation.mutate,
    saving: mutation.isPending,
    error,
    /** Ohne Antwort ist offen, ob gespeichert wurde. */
    unanswered,
    reset: () => {
      setError(null);
      setUnanswered(false);
    },
  };
}
